import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Box, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, Textarea, Button, useDisclosure, FormControl, FormLabel, Spinner, Text, VStack, Flex, Input, Tabs, TabList, TabPanels, Tab, TabPanel } from "@chakra-ui/react";
import Artifacts from "components/agentChat/Artifacts";
import SplitView from "components/common/SplitView";
import Chat, { ChatMessage } from "components/agentChat/Chat";
import { deleteChatSession, duplicateChatSession, fetchChatSession, saveSession } from "services/AgentChatService";
import { ArtifactProto, ChatClientType, chatClientTypeFromJSON, chatClientTypeToJSON, ExecutionStepProto } from "build-ts/chat_session";
import { artifactTypeFromJSON, ChatMessageType, chatMessageTypeFromJSON, DarwinHeader, ExecutionStatusType } from "build-ts/common";
import { NodeInsights } from "containers/Interfaces";
import { AnalysisTreeNodeProto } from "build-ts/analysis";
import { getKGObservationData } from "services/AnalysisTreeServices";
import { ArtifactType } from "build-ts/common";
import showToast from "hooks/useCustomToast";
import { populateArtifact } from "services/DataService";
import { DataFrameAnalysisProto_ParentHeader, KGDataFrameProto } from "build-ts/dataframe";
import CopyButton from "components/common/CopyButton";
import { useAnalystChat } from "context/AnalystChatContext";
import { useURLContext } from "context/URLContext";
import { useUser } from "context/UserContext";
import ChatNameComponent from "components/agentChat/ChatNameComponent";
import { ScheduledJobProto, ScheduledJobType } from "build-ts/schedule";
import { listScheduledJobsByOwnerId } from "services/ScheduleServices";

interface ChatPageProps {
	systemPrompt?: string;
	/** Optional context template (e.g. from EVA agent) shown in View prompt modal */
	contextTemplate?: string;
	// Eva flags, true when rendered from any two components of Eva (both Saved and Unsaved chat)
	isFromEva?: boolean;
	initialChatSessionHeader?: DarwinHeader; // Session Header from EvaPlayground
	// Scenario creation mode (when set, chat is used for scenario creation with analyst/scenario context)
	/** The analyst id we navigated from (used to set selected analyst) */
	initialAnalystId?: string;
	// Selected scenario ids from the chat menu
	selectedScenarioIds?: string[];
	/** If set, chat will initialize a session and send this as the first user message */
	initialPrompt?: string;
}

const ChatPage: React.FC<ChatPageProps> = ({
	systemPrompt = "",
	contextTemplate,
	isFromEva = false,
	initialChatSessionHeader,
	initialAnalystId,
	selectedScenarioIds,
	initialPrompt: initialPromptProp,
}) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { id: chatSessionId } = useParams();
	const { defaultAnalystId, userId } = useUser();
	// Check if this is a new chat route
	const isNewChatRoute = location.pathname === "/chat/new";
	const isEvaChatRoute = location.pathname.startsWith("/eva/chat");

	// Get chat context
	const {
		fetchChatSessionsList,
		fetchEvaChatSessionsList,
		selectedAnalyst,
		setSelectedAnalyst,
		analystsList,
		isLoadingAnalysts,
		setCurrentChatId,
		setIsNavbarCollapsed,
		currentSessionHeader,
		setCurrentSessionHeader,
	} = useAnalystChat();

	// Get chatAnalystId from URL context (for navigation from AnalystPage)
	const { chatAnalystId } = useURLContext();

	// Handle chatAnalystId from URL (when navigating from AnalystPage's Chat button)
	useEffect(() => {
		// Only process if chatAnalystId is in the current URL query params
		const urlHasChatAnalystId = location.search.includes("chatAnalystId");

		if (!urlHasChatAnalystId || !chatAnalystId || isFromEva) {
			return;
		}

		// Wait for analysts list to be loaded
		if (isLoadingAnalysts || analystsList.length === 0) {
			return;
		}

		// Find the analyst by ID
		const analyst = analystsList.find(a => a.id === chatAnalystId);

		if (analyst) {
			// Set the analyst and navigate to /chat/new to create a new session
			setSelectedAnalyst(analyst);
			navigate("/chat/new", { replace: true, state: { preserveAnalyst: true } });
		} else {
			// Analyst not found - show error and navigate to /chat/new without selection
			showToast("Analyst not found", "", "error");
			navigate("/chat/new", { replace: true });
		}
	}, [chatAnalystId, analystsList, isLoadingAnalysts, isFromEva]);

	// Handle analyst selection
	const handleAnalystChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const analystId = e.target.value;
		const analyst = analystsList.find(a => a.id === analystId);
		setSelectedAnalyst(analyst || null);
		navigate("/chat/new");
	};

	// Loading state for chat
	const [isLoadingChat, setIsLoadingChat] = useState(false);
	const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
	// Store all artifacts from all messages for dataframe link matching
	const [allArtifacts, setAllArtifacts] = useState<ArtifactProto[]>([]);

	// System prompt modal state
	const { isOpen: isSystemPromptModalOpen, onOpen: onSystemPromptModalOpen, onClose: onSystemPromptModalClose } = useDisclosure();
	// Save chat modal state
	const { isOpen: isSaveModalOpen, onOpen: onSaveModalOpen, onClose: onSaveModalClose } = useDisclosure();
	const [saveChatTitle, setSaveChatTitle] = useState("");
	const [saveChatDescription, setSaveChatDescription] = useState("");
	// Artifacts panel state
	const [isArtifactsPanelVisible, setIsArtifactsPanelVisible] = useState(false);
	const [isArtifactExpanded, setIsArtifactExpanded] = useState(false);
	const [selectedArtifacts, setSelectedArtifacts] = useState<{
		artifact?: ArtifactProto,
		nodeInsights?: NodeInsights,
		kgDataFrameData?: KGDataFrameProto,
		langgraphMessage?: string[],
		orderIndex: number,
		loading: boolean,
		analysisGroupId?: string,
		dataframeAnalysisView?: {
			activeTab?: string;
			selectedItemIndex?: number;
		},
	}[]>([]);
	const [isLoadingArtifact, setIsLoadingArtifact] = useState(false);
	// Signal to Artifacts panel to activate a specific tab without moving it
	const [activateArtifact, setActivateArtifact] = useState<{ orderIndex: number; tick: number } | undefined>(undefined);
	const orderCounter = useRef(0);
	// Tracks the current ANALYSIS_OUTPUT group being built from a single click event.
	// Multiple onArtifactClick calls from the same forEach share one groupId, then it's
	// cleared via setTimeout so the next independent click starts a fresh group.
	const pendingAnalysisGroupRef = useRef<{ groupId: string; timer: ReturnType<typeof setTimeout> } | null>(null);
	// Scenario jobs for the chat (loaded on session init + updated after manual/agent saves)
	const [scenarioJobs, setScenarioJobs] = useState<ScheduledJobProto[]>([]);
	// Track the last session ID we actually started loading to prevent duplicate fetches
	// when dependencies like analystsList change reference without the session ID changing.
	const lastFetchedSessionIdRef = useRef<string | null>(null);
	// Effective analyst id for scenario operations (from props or loaded chat session)
	const [resolvedAnalystId, setResolvedAnalystId] = useState<string | undefined>(initialAnalystId);
	// Detect scenario jobs saved by the agent via tool calls (tool name includes "save_job") in the last message of the response.
	const [scenarioJobsSavedByTools, setScenarioJobsSavedByTools] = useState<ScheduledJobProto[]>([]);

	// Keep selectedAnalyst in sync with resolvedAnalystId for existing chats
	useEffect(() => {
		if (!resolvedAnalystId || analystsList.length === 0 || isNewChatRoute || isFromEva) return;

		const analyst = analystsList.find(a => a.id === resolvedAnalystId);
		if (analyst) {
			setSelectedAnalyst(analyst);
		}
	}, [resolvedAnalystId, analystsList, isNewChatRoute, setSelectedAnalyst, isFromEva]);

	// Set of job header ids already persisted (for Saved vs Draft badge on ScenarioArtifactCard)
	const savedJobIds = useMemo(() => {
		const ids = new Set<string>();
		scenarioJobs.forEach((job) => {
			if (job.header?.id) ids.add(job.header.id);
		});
		return ids;
	}, [scenarioJobs]);

	// Merge agent-saved jobs into scenarioJobs, allArtifacts, selectedArtifacts, and chatMessages.
	useEffect(() => {
		if (scenarioJobsSavedByTools.length === 0) return;
		setScenarioJobs((prev) => {
			let updated = [...prev];
			for (const job of scenarioJobsSavedByTools) {
				if (!job.header?.id) continue;
				const idx = updated.findIndex((j) => j.header?.id === job.header!.id);
				if (idx >= 0) {
					updated = [...updated.slice(0, idx), job, ...updated.slice(idx + 1)];
				} else {
					updated = [...updated, job];
				}
			}
			return updated;
		});
		setAllArtifacts((prev) =>
			prev.map((a) => {
				if (artifactTypeFromJSON(a.artifact_type) !== ArtifactType.SCENARIO_JOB) return a;
				const match = scenarioJobsSavedByTools.find(
					(j) => j.header?.id && j.header.id === a.scenario_job?.header?.id
				);
				return match ? { ...a, scenario_job: match } : a;
			})
		);
		setSelectedArtifacts((prev) =>
			prev.map((item) => {
				if (artifactTypeFromJSON(item.artifact?.artifact_type) !== ArtifactType.SCENARIO_JOB) return item;
				const match = scenarioJobsSavedByTools.find(
					(j) => j.header?.id && j.header.id === item.artifact?.scenario_job?.header?.id
				);
				return match ? { ...item, artifact: { ...item.artifact!, scenario_job: match } } : item;
			})
		);
		setChatMessages((prev) =>
			prev.map((msg) => {
				if (!msg.artifacts) return msg;
				const updatedArtifacts = msg.artifacts.map((a) => {
					if (artifactTypeFromJSON(a.artifact_type) !== ArtifactType.SCENARIO_JOB) return a;
					const match = scenarioJobsSavedByTools.find(
						(j) => j.header?.id && j.header.id === a.scenario_job?.header?.id
					);
					return match ? { ...a, scenario_job: match } : a;
				});
				return { ...msg, artifacts: updatedArtifacts };
			})
		);
	}, [scenarioJobsSavedByTools]);

	const [chatId, setChatId] = useState<string | undefined>(undefined);
	const [isSaving, setIsSaving] = useState(false);
	// System prompt state - initialized from prop, can be updated from saved chat
	const [currentSystemPrompt, setCurrentSystemPrompt] = useState<string>(systemPrompt);
	// Context message (resolved) shown in View prompt modal; can come from EVA or from first CONTEXT message
	const [currentContext, setCurrentContext] = useState<string | undefined>(contextTemplate);
	// Chat is saved if session header has created_date
	const [isChatSaved, setIsChatSaved] = useState(false);
	// Handle artifact click
	const handleArtifactClick = async (artifact: ArtifactProto, activeTab?: string, selectedItemIndex?: number) => {
		const dataframeAnalysisView =
			activeTab || selectedItemIndex !== undefined
				? {
					activeTab,
					selectedItemIndex,
				}
				: undefined;
		const getArtifactStableId = (artifact?: ArtifactProto): string | undefined => {
			if (!artifact) return undefined;
			return (
				artifact.dataframe_analysis?.header?.id ||
				artifact.kg_dataframe?.header?.id ||
				artifact.scenario_job?.header?.id ||
				artifact.analysis_output?.header?.id
			);
		};
		const incomingType = artifactTypeFromJSON(artifact.artifact_type);
		const incomingId = getArtifactStableId(artifact);
		// Check if artifact already selected
		const existingIndex = selectedArtifacts.findIndex((item) => {
			if (!item.artifact) return false;
			if (artifactTypeFromJSON(item.artifact.artifact_type) !== incomingType) return false;
			const selectedId = getArtifactStableId(item.artifact);
			return !!incomingId && selectedId === incomingId;
		});

		if (
			existingIndex !== -1 &&
			incomingType !== ArtifactType.DATAFRAME_ANALYSIS &&
			incomingType !== ArtifactType.KG_DATAFRAME
		) {
			// Tab already exists — activate it in place without moving it
			const existingItem = selectedArtifacts[existingIndex];
			if (existingItem) {
				setActivateArtifact(prev => ({ orderIndex: existingItem.orderIndex, tick: (prev?.tick ?? 0) + 1 }));
			}
			setIsArtifactsPanelVisible(true);
			setIsNavbarCollapsed(true);
			return;
		}

		const artifactType = artifactTypeFromJSON(artifact.artifact_type);

		// Handle KG_DATAFRAME
		if (artifactType === ArtifactType.KG_DATAFRAME) {
			if (!artifact.kg_dataframe) {
				showToast("Missing kg_dataframe data", "", "error");
				return;
			}

			// Check if any column has data_value_json_array
			const hasData = artifact.kg_dataframe.ordered_columns.some(
				column => column.data_value_json_array
			);

			// Show panel and set loading
			setIsNavbarCollapsed(true);
			setIsArtifactsPanelVisible(true);

			if (hasData) {
				// Data is already present, upsert the artifact in-place when it already exists.
				if (existingIndex !== -1) {
					const existingItem = selectedArtifacts[existingIndex];
					if (existingItem) {
						setSelectedArtifacts(prev =>
							prev.map((item, idx) => (
								idx === existingIndex
									? { ...item, artifact, kgDataFrameData: artifact.kg_dataframe, loading: false }
									: item
							))
						);
						setActivateArtifact(prev => ({ orderIndex: existingItem.orderIndex, tick: (prev?.tick ?? 0) + 1 }));
					}
				} else {
					orderCounter.current++;
					setSelectedArtifacts(prev => [...prev, {
						artifact,
						kgDataFrameData: artifact.kg_dataframe,
						orderIndex: orderCounter.current,
						loading: false
					}]);
				}
			} else {
				// No data present, need to fetch it
				setIsLoadingArtifact(true);

				try {
					const artifactRequest = ArtifactProto.create({
						artifact_type: ArtifactType.KG_DATAFRAME,
						kg_dataframe: artifact.kg_dataframe,
					});
					const response = await populateArtifact(artifactRequest);
					const dataframeData = response?.kg_dataframe;

					if (dataframeData) {
						if (existingIndex !== -1) {
							const existingItem = selectedArtifacts[existingIndex];
							if (existingItem) {
								setSelectedArtifacts(prev =>
									prev.map((item, idx) => (
										idx === existingIndex
											? { ...item, artifact, kgDataFrameData: dataframeData, loading: false }
											: item
									))
								);
								setActivateArtifact(prev => ({ orderIndex: existingItem.orderIndex, tick: (prev?.tick ?? 0) + 1 }));
							}
						} else {
							orderCounter.current++;
							setSelectedArtifacts(prev => [...prev, {
								artifact,
								kgDataFrameData: dataframeData,
								orderIndex: orderCounter.current,
								loading: false
							}]);
						}
					} else {
						showToast("Failed to fetch dataframe data", "", "error");
					}
				} catch (error) {
					console.error("Error fetching dataframe data:", error);
					showToast("Error fetching dataframe data", "", "error");
				} finally {
					setIsLoadingArtifact(false);
				}
			}
			return;
		}

		// Handle KG_OBSERVATION
		if (artifactType === ArtifactType.KG_OBSERVATION) {
			if (!artifact.kg_observation) {
				showToast("Missing kg_observation data", "", "error");
				return;
			}

			// Show panel and set loading
			setIsLoadingArtifact(true);
			setIsArtifactsPanelVisible(true);

			try {
				// Call API with single observation
				const response = await getKGObservationData([artifact.kg_observation]);

				if (response && response.length > 0 && response[0].kg_observation_data) {
					// Build NodeInsights
					const nodeInsights: NodeInsights = {
						current_node: AnalysisTreeNodeProto.create({
							kg_observation: artifact.kg_observation,
							kg_observation_data: response[0].kg_observation_data
						}),
						insights: undefined
					};

					// Add to selected artifacts
					orderCounter.current++;
					setSelectedArtifacts(prev => [...prev, {
						artifact,
						nodeInsights,
						orderIndex: orderCounter.current,
						loading: false
					}]);
				}
			} catch (error) {
				console.error("Error fetching artifact data:", error);
			} finally {
				setIsLoadingArtifact(false);
			}
			return;
		}

		// Handle SCENARIO_JOB: deduplicate by job id, then add / bring to top.
		if (artifactType === ArtifactType.SCENARIO_JOB) {
			if (!artifact.scenario_job) {
				showToast("Missing scenario_job data", "", "error");
				return;
			}
			const incomingJobId = artifact.scenario_job.header?.id;
			const duplicateIndex = incomingJobId
				? selectedArtifacts.findIndex(
					item =>
						artifactTypeFromJSON(item.artifact?.artifact_type) === ArtifactType.SCENARIO_JOB &&
						item.artifact?.scenario_job?.header?.id === incomingJobId
				)
				: -1;

			if (duplicateIndex !== -1) {
				const existingItem = selectedArtifacts[duplicateIndex];
				if (existingItem?.artifact) handleRemoveArtifact(existingItem.artifact);
				orderCounter.current++;
				setSelectedArtifacts(prev => [...prev, {
					artifact,
					orderIndex: orderCounter.current,
					loading: false
				}]);
				setIsArtifactsPanelVisible(true);
				setIsNavbarCollapsed(true);
				return;
			}
			orderCounter.current++;
			setSelectedArtifacts(prev => [...prev, {
				artifact,
				orderIndex: orderCounter.current,
				loading: false
			}]);
			setIsArtifactsPanelVisible(true);
			setIsNavbarCollapsed(true);
			return;
		}

		// Handle ANALYSIS_OUTPUT — artifacts from the same forEach click share a groupId.
		if (artifactType === ArtifactType.ANALYSIS_OUTPUT) {
			if (!artifact.analysis_output) {
				showToast("Missing analysis_output data", "", "error");
				return;
			}

			// Reuse or create a pending group for artifacts arriving in the same synchronous batch.
			if (!pendingAnalysisGroupRef.current) {
				const groupId = `group-${Date.now()}-${Math.random().toString(36).slice(2)}`;
				const timer = setTimeout(() => { pendingAnalysisGroupRef.current = null; }, 0);
				pendingAnalysisGroupRef.current = { groupId, timer };
			}
			const analysisGroupId = pendingAnalysisGroupRef.current.groupId;

			const incomingOutputId = artifact.analysis_output.header?.id;
			const duplicateIndex = incomingOutputId
				? selectedArtifacts.findIndex(
					item =>
						artifactTypeFromJSON(item.artifact?.artifact_type) === ArtifactType.ANALYSIS_OUTPUT &&
						item.artifact?.analysis_output?.header?.id === incomingOutputId
				)
				: -1;

			if (duplicateIndex !== -1) {
				const existingItem = selectedArtifacts[duplicateIndex];
				if (existingItem?.artifact) handleRemoveArtifact(existingItem.artifact);
			}
			orderCounter.current++;
			setSelectedArtifacts(prev => [...prev, {
				artifact,
				orderIndex: orderCounter.current,
				loading: false,
				analysisGroupId,
			}]);
			setIsArtifactsPanelVisible(true);
			setIsNavbarCollapsed(true);
			return;
		}

		// Handle DATAFRAME_ANALYSIS
		if (artifactType === ArtifactType.DATAFRAME_ANALYSIS) {
			if (!artifact.dataframe_analysis) {
				showToast("Missing dataframe_analysis data", "", "error");
				return;
			}

			// Populate when dataframes are missing OR chart specs are structure-only (no injected data yet).
			const hasData = artifact.dataframe_analysis.pandas_dataframes.some((df) => !!df.data_json);
			const charts = (
				artifact.dataframe_analysis as unknown as { charts?: Array<{ spec_json?: string }> } | undefined
			)?.charts ?? [];
			const hasChartNeedingHydration = charts.some((chart) => {
				const raw = chart.spec_json?.trim();
				if (!raw) return false;
				try {
					const parsed = JSON.parse(raw) as Record<string, unknown>;
					return parsed.data === undefined;
				} catch {
					return false;
				}
			});
			const shouldPopulate = !hasData || hasChartNeedingHydration;
			const upsertDataframeAnalysisArtifact = (nextArtifact: ArtifactProto) => {
				if (existingIndex !== -1) {
					const existingItem = selectedArtifacts[existingIndex];
					if (existingItem) {
						setSelectedArtifacts(prev =>
							prev.map((item, idx) => (
								idx === existingIndex
									? {
										...item,
										artifact: nextArtifact,
										loading: false,
										dataframeAnalysisView: dataframeAnalysisView ?? item.dataframeAnalysisView,
									}
									: item
							))
						);
						setActivateArtifact(prev => ({ orderIndex: existingItem.orderIndex, tick: (prev?.tick ?? 0) + 1 }));
					}
					return;
				}
				orderCounter.current++;
				setSelectedArtifacts(prev => [...prev, {
					artifact: nextArtifact,
					orderIndex: orderCounter.current,
					loading: false,
					dataframeAnalysisView,
				}]);
			};

			setIsNavbarCollapsed(true);
			setIsArtifactsPanelVisible(true);

			if (!shouldPopulate) {
				upsertDataframeAnalysisArtifact(artifact);
			} else {
				setIsLoadingArtifact(true);
				try {
					if (!artifact.chat_id && !chatId) {
						showToast("Missing chat id for dataframe analysis hydration", "", "error");
						return;
					}
					const artifactRequest = ArtifactProto.create({
						artifact_type: ArtifactType.DATAFRAME_ANALYSIS,
						dataframe_analysis: artifact.dataframe_analysis,
						chat_id: artifact.chat_id || chatId,
					});
					const response = await populateArtifact(artifactRequest);
					const analysisData = response?.dataframe_analysis;
					if (analysisData) {
						upsertDataframeAnalysisArtifact({ ...artifact, dataframe_analysis: analysisData });
					} else {
						showToast("Failed to fetch dataframe analysis data", "", "error");
					}
				} catch (error) {
					console.error("Error fetching dataframe analysis data:", error);
					showToast("Error fetching dataframe analysis data", "", "error");
				} finally {
					setIsLoadingArtifact(false);
				}
			}
			return;
		}

		// Unsupported artifact type
		showToast("Artifact type not supported yet", "", "error");
	};

	// Handle "Jump to source" click from a DataFrameAnalysis lineage tab.
	// Finds the parent DataFrameAnalysis artifact in allArtifacts by its header id,
	// then delegates to handleArtifactClick which handles populate-if-needed + display.
	const handleSourceClick = async (ph: DataFrameAnalysisProto_ParentHeader) => {
		const parentId = ph.header?.id;
		if (!parentId) {
			showToast("Source has no identifier", "", "error");
			return;
		}
		const artifact = allArtifacts.find(
			(a) =>
				(a.dataframe_analysis?.header?.id === parentId && artifactTypeFromJSON(a.artifact_type) === ArtifactType.DATAFRAME_ANALYSIS) ||
				(a.kg_dataframe?.header?.id === parentId && artifactTypeFromJSON(a.artifact_type) === ArtifactType.KG_DATAFRAME)
		);
		if (!artifact) {
			showToast("Source dataframe not found in this session", "", "error");
			return;
		}
		await handleArtifactClick(artifact);
	};

	// Effect to close artifacts panel when loading chat
	useEffect(() => {
		if (isLoadingChat) {
			handleCloseArtifactsPanel();
			setSelectedArtifacts([]);
		}
	}, [isLoadingChat]);

	// Handle closing artifacts panel
	const handleCloseArtifactsPanel = () => {
		setIsArtifactsPanelVisible(false);
		setIsArtifactExpanded(false);
	};

	// Handle clearing all artifacts
	const handleClearAllArtifacts = () => {
		setSelectedArtifacts([]);
	};

	// Handle removing a single artifact
	const handleRemoveArtifact = (artifactToRemove: ArtifactProto) => {
		setSelectedArtifacts(prev => prev.filter(item => item.artifact !== artifactToRemove));
	};

	// Called by ScenarioArtifactCard after a job is manually saved; keeps all job state in sync.
	const handleJobSave = (savedJob: ScheduledJobProto) => {
		if (!savedJob.header?.id) return;
		const jobId = savedJob.header.id;
		setScenarioJobs((prev) => {
			const idx = prev.findIndex((j) => j.header?.id === jobId);
			if (idx >= 0) {
				return [...prev.slice(0, idx), savedJob, ...prev.slice(idx + 1)];
			}
			return [...prev, savedJob];
		});
		setAllArtifacts((prev) =>
			prev.map((a) =>
				artifactTypeFromJSON(a.artifact_type) === ArtifactType.SCENARIO_JOB &&
					a.scenario_job?.header?.id === jobId
					? { ...a, scenario_job: savedJob }
					: a
			)
		);
		setSelectedArtifacts((prev) =>
			prev.map((item) =>
				artifactTypeFromJSON(item.artifact?.artifact_type) === ArtifactType.SCENARIO_JOB &&
					item.artifact?.scenario_job?.header?.id === jobId
					? { ...item, artifact: { ...item.artifact!, scenario_job: savedJob } }
					: item
			)
		);
		setChatMessages((prev) =>
			prev.map((msg) => {
				if (!msg.artifacts) return msg;
				const updatedArtifacts = msg.artifacts.map((a) =>
					artifactTypeFromJSON(a.artifact_type) === ArtifactType.SCENARIO_JOB &&
						a.scenario_job?.header?.id === jobId
						? { ...a, scenario_job: savedJob }
						: a
				);
				return { ...msg, artifacts: updatedArtifacts };
			})
		);
	};

	// Update currentSystemPrompt when systemPrompt prop changes (for playground)
	useEffect(() => {
		if (systemPrompt) {
			setCurrentSystemPrompt(systemPrompt);
		}
	}, [systemPrompt]);

	// Keep currentContext in sync with optional contextTemplate prop (e.g. from EVA Playground)
	useEffect(() => {
		setCurrentContext(contextTemplate);
	}, [contextTemplate]);

	// Set chatId from initialChatSessionId when coming from EvaPlayground
	useEffect(() => {
		if (isFromEva && !isChatSaved && initialChatSessionHeader && !chatId) {
			setChatId(initialChatSessionHeader.id);
			setCurrentSessionHeader(initialChatSessionHeader);
		}
	}, [isFromEva, isChatSaved, initialChatSessionHeader, chatId, navigate]);

	// This is used to clean the content of the chat messages from the ```markdown or ``` wrapper
	const cleanContent = (raw: string): string => {
		// Remove ```markdown or ``` wrapper
		return raw.replace(/^```(?:markdown)?\n?/, "").replace(/\n?```$/, "");
	};

	// Function to fetch the chat history - new darwin
	const fetchChatHistory = async (sessionId: string) => {
		// Fetch header only first to check client type — no messages, no transaction init.
		const headerResponse = await fetchChatSession(sessionId, false, false);
		const clientType = chatClientTypeFromJSON(headerResponse?.session_header?.type);
		const targetClientType = isFromEva ? ChatClientType.EVA : ChatClientType.FRIDA;
		const isMatchingType = clientType === targetClientType;

		let chatResponse;
		if (!isMatchingType) {
			// Client type doesn't match the current context — duplicate with the correct type.
			// e.g. FRIDA session opened in EVA context → duplicate as EVA, and vice versa.
			const duplicated = await duplicateChatSession({
				originalSessionId: sessionId,
				currentUserId: userId ?? "",
				clientType: chatClientTypeToJSON(targetClientType),
				save: false,
				initTransaction: true,
			});
			if (!duplicated?.session_header?.id) return;
			window.history.replaceState(null, "", `${isFromEva ? "/eva" : ""}/chat/${duplicated.session_header.id}`);
			chatResponse = duplicated;
			if (duplicated.analyst_id) {
				const jobs = await listScheduledJobsByOwnerId(duplicated.analyst_id);
				if (jobs && jobs.length > 0) {
					setScenarioJobs(jobs.filter((job: ScheduledJobProto) => job.type === ScheduledJobType.SCENARIO_JOB));
				}
			}
		} else {
			chatResponse = await fetchChatSession(sessionId, true, true);
			const analystId = chatResponse?.analyst_id;
			if (analystId) {
				const jobs = await listScheduledJobsByOwnerId(analystId);
				if (jobs && jobs.length > 0) {
					setScenarioJobs(jobs.filter((job: ScheduledJobProto) => job.type === ScheduledJobType.SCENARIO_JOB));
				}
			}
		}

		if (chatResponse?.session_header?.id) {
			setChatId(chatResponse.session_header.id);
			setCurrentChatId(chatResponse.session_header.id);
			setCurrentSessionHeader(chatResponse.session_header);
			if (chatResponse.session_header.created_date) {
				setIsChatSaved(true);
			}
		}

		// Resolve analyst id from existing chat session; keep it in sync when switching chats
		if (chatResponse?.analyst_id && chatResponse.analyst_id !== resolvedAnalystId) {
			setResolvedAnalystId(chatResponse.analyst_id);
		}

		// Check the status of the chat
		if (chatResponse?.status) {
			if (chatResponse.status === ExecutionStatusType.ERROR_STATE) {
				showToast("Error in Chat.", "Please start a new chat.", "error");
			}
		}

		// Extract system prompt and context from first SYSTEM / CONTEXT messages for saved chats
		let systemPromptFromMessage = "";
		let contextFromMessage = "";
		const convertedMessages: ChatMessage[] = [];
		const collectedArtifacts: ArtifactProto[] = [];
		let pendingArtifacts: ArtifactProto[] = [];
		let pendingExecutionSteps: ExecutionStepProto[] = [];
		let langgraphMessageJsonList: string[] = [];

		for (const msg of chatResponse.messages) {
			const messageType = chatMessageTypeFromJSON(msg.message_type);

			// Extract system prompt from first SYSTEM message
			if (messageType === ChatMessageType.SYSTEM && msg.content && !systemPromptFromMessage) {
				systemPromptFromMessage = msg.content;
			}
			// Extract context from first CONTEXT message (stored for the View Prompt modal only).
			else if (messageType === ChatMessageType.CONTEXT && msg.content && !contextFromMessage) {
				contextFromMessage = msg.content;
			}
			else if (messageType === ChatMessageType.HUMAN && msg.content) {
				convertedMessages.push({
					author: "user",
					content: cleanContent(msg.content),
					userTimestampMs: msg.timestamp_ms
				});
			}
			else if (messageType === ChatMessageType.REASONING) {
				const steps = msg.execution_steps;
				pendingArtifacts = steps.flatMap(s => s.artifacts);
				pendingExecutionSteps = steps;
				langgraphMessageJsonList = msg.langgraph_message_json_list && msg.langgraph_message_json_list.length > 0 ? msg.langgraph_message_json_list : [];

				for (const artifact of pendingArtifacts) {
					collectedArtifacts.push(artifact);
				}
			}
			else if (messageType === ChatMessageType.RESPONSE && msg.content) {
				convertedMessages.push({
					author: "ai",
					content: cleanContent(msg.content),
					artifacts: pendingArtifacts.length > 0 ? pendingArtifacts : undefined,
					executionSteps: pendingExecutionSteps.length > 0 ? pendingExecutionSteps : undefined,
					ls_trace_url: msg.ls_trace_url,
					langgraph_message_json_list: langgraphMessageJsonList
				});
				pendingArtifacts = [];
				pendingExecutionSteps = [];
				langgraphMessageJsonList = [];
			}
			else if (messageType === ChatMessageType.RESPONSE && msg.response_message) {
				convertedMessages.push({
					author: "ai",
					content: "",
					responseMessage: msg.response_message,
					artifacts: pendingArtifacts.length > 0 ? pendingArtifacts : undefined,
					executionSteps: pendingExecutionSteps.length > 0 ? pendingExecutionSteps : undefined,
					ls_trace_url: msg.ls_trace_url,
					langgraph_message_json_list: langgraphMessageJsonList
				});
				pendingArtifacts = [];
				pendingExecutionSteps = [];
				langgraphMessageJsonList = [];
			}
		}
		// Update system prompt and context from API if found (for saved chats).
		// Always call setCurrentContext so stale context from a previous session is cleared
		// when the new session has no CONTEXT message.
		if (systemPromptFromMessage) {
			setCurrentSystemPrompt(systemPromptFromMessage);
		}
		setCurrentContext(contextFromMessage || undefined);
		setChatMessages(convertedMessages);
		// Only add unique SCENARIO_JOB artifacts to allArtifacts
		const deduplicatedArtifacts: ArtifactProto[] = [];
		for (const artifact of collectedArtifacts) {
			if (artifactTypeFromJSON(artifact.artifact_type) === ArtifactType.SCENARIO_JOB && artifact.scenario_job?.header?.id) {
				const existingIdx = deduplicatedArtifacts.findIndex(
					(a) =>
						artifactTypeFromJSON(a.artifact_type) === ArtifactType.SCENARIO_JOB &&
						a.scenario_job?.header?.id === artifact.scenario_job!.header!.id
				);
				if (existingIdx >= 0) {
					deduplicatedArtifacts[existingIdx] = artifact;
				} else {
					deduplicatedArtifacts.push(artifact);
				}
			} else {
				deduplicatedArtifacts.push(artifact);
			}
		}
		setAllArtifacts(deduplicatedArtifacts);
	};

	// Effect to handle URL changes: /chat/new or /chat/:id or /eva/chat/:id (skip in scenario creation mode)
	useEffect(() => {
		// Handle EVA Chat case
		if (isEvaChatRoute && chatSessionId && chatSessionId !== lastFetchedSessionIdRef.current) {
			lastFetchedSessionIdRef.current = chatSessionId;
			setChatId(chatSessionId);
			setIsLoadingChat(true);
			fetchChatHistory(chatSessionId)
				.catch(error => console.error("Error loading chat session:", error))
				.finally(() => setIsLoadingChat(false));
			return;
		}

		// Handle /chat/new route - initialize a new chat session
		if (isNewChatRoute) {
			lastFetchedSessionIdRef.current = null;
			setChatMessages([]);
			setAllArtifacts([]);
			setSelectedArtifacts([]);
			setIsArtifactsPanelVisible(false);
			setChatId(undefined);
			setIsChatSaved(false);
			setCurrentContext(undefined);
			setCurrentSessionHeader(undefined);
			setCurrentChatId("");
			// this check is to check whether it is coming from the analyst page or not.
			if (!location.state?.preserveAnalyst) {
				setSelectedAnalyst(analystsList.find(analyst => analyst.id === defaultAnalystId) || analystsList[0]);
			}
			setScenarioJobs([]);
			setAllArtifacts([]);
			setSelectedArtifacts([]);
			setScenarioJobsSavedByTools([]);
			return;
		}

		// Handle /chat/:id route - load existing chat session
		if (chatSessionId && chatSessionId !== lastFetchedSessionIdRef.current && chatSessionId !== currentSessionHeader?.id && !isEvaChatRoute) {
			lastFetchedSessionIdRef.current = chatSessionId;

			// If chatId is already set to this session it means Chat.tsx just created it
			// (new-chat flow: Step 1 sets chatId + navigates before the AI response arrives).
			// The session is already live in memory with the user's optimistic messages, so
			// fetching from the DB (which has 0 messages at this point) would wipe everything.
			if (chatId === chatSessionId) {
				return;
			}

			setIsLoadingChat(true);
			fetchChatHistory(chatSessionId)
				.catch(error => console.error("Error loading chat session:", error))
				.finally(() => setIsLoadingChat(false));
		}
	}, [isNewChatRoute, chatSessionId, analystsList, initialChatSessionHeader]);


	return (
		<Box height="100%" width="100%" overflow="hidden" position="relative" display="flex" flexDirection="column">

			{/* Main Content Area */}
			<Box
				flex={1}
				width="100%"
				transition="margin-left 0.3s ease-in-out, width 0.3s ease-in-out"
				overflow="hidden"
			>
				<SplitView
					leftPanel={
						<Box
							height="100%"
							width="100%"
							bg="bg.surface"
							overflow="hidden"
							display="flex"
							flexDirection="column"
						>
							{currentSessionHeader && (
								<ChatNameComponent
									chatId={currentSessionHeader?.id || ""}
									sessionHeader={currentSessionHeader}
									setSessionHeader={setCurrentSessionHeader}
									onAfterRename={() => isFromEva ? fetchEvaChatSessionsList("") : fetchChatSessionsList(ChatClientType.FRIDA, "")}
									onDelete={() => {
										if (!currentSessionHeader?.id) return;
										deleteChatSession(currentSessionHeader.id).then(() => {
											if (isFromEva) {
												fetchEvaChatSessionsList("");
												navigate("/eva/agents");
											} else {
												fetchChatSessionsList(ChatClientType.FRIDA, "");
												navigate("/chat/new");
											}
										});
									}}
									onToggleArtifacts={() => setIsArtifactsPanelVisible((v) => !v)}
									isArtifactsPanelVisible={isArtifactsPanelVisible}
									analystsList={analystsList}
									selectedAnalyst={selectedAnalyst}
									onAnalystChange={(analystId) => handleAnalystChange({ target: { value: analystId } } as React.ChangeEvent<HTMLSelectElement>)}
									scenarioJobs={scenarioJobs}
									isFromEva={isFromEva}
									onViewPrompt={isFromEva ? onSystemPromptModalOpen : undefined}
									isChatSaved={isChatSaved}
									hasChatMessages={chatMessages.length > 0}
									onSaveChat={isFromEva ? onSaveModalOpen : undefined}
								/>
							)}
							<Box flex={1} overflow="hidden">
								<Chat
									userId={userId}
									chatMessages={chatMessages}
									chatId={chatId}
									setChatId={setChatId}
									setChatMessages={setChatMessages}
									isLoadingChat={isLoadingChat}
									sessionId={isFromEva && !isChatSaved ? initialChatSessionHeader?.id : chatSessionId}
									fetchChatSessionsList={fetchChatSessionsList}
									fetchEvaChatSessionsList={fetchEvaChatSessionsList}
									onArtifactClick={handleArtifactClick}
									selectedAnalyst={selectedAnalyst}
									isFromEva={isFromEva}
									cleanContent={cleanContent}
									analystsList={analystsList}
									isLoadingAnalysts={isLoadingAnalysts}
									onAnalystChange={handleAnalystChange}
									isArtifactsPanelVisible={isArtifactsPanelVisible}
									allArtifacts={allArtifacts}
									setAllArtifacts={setAllArtifacts}
									isChatSaved={isChatSaved}
									selectedScenarioIds={selectedScenarioIds ?? []}
									initialPrompt={initialPromptProp}
									scenarioJobs={scenarioJobs}
									setScenarioJobs={setScenarioJobs}
									setScenarioJobsSavedByTools={setScenarioJobsSavedByTools}
								/>
							</Box>
						</Box>
					}
					rightPanel={
						<Artifacts
							selectedArtifacts={selectedArtifacts}
							isLoadingArtifact={isLoadingArtifact}
							onClosePanel={handleCloseArtifactsPanel}
							onClearAll={handleClearAllArtifacts}
							onRemoveArtifact={handleRemoveArtifact}
							savedJobIds={savedJobIds}
							onJobSave={handleJobSave}
							analystId={resolvedAnalystId ?? selectedAnalyst?.id}
							onSourceClick={handleSourceClick}
							activateArtifact={activateArtifact}
							allArtifacts={allArtifacts}
							onArtifactClick={handleArtifactClick}
							isArtifactExpanded={isArtifactExpanded}
							onToggleExpand={() => setIsArtifactExpanded((v) => !v)}
						/>
					}
					isRightPanelVisible={isArtifactsPanelVisible}
					defaultRightWidth={45}
					expandRightPanel={isArtifactExpanded}
				/>
			</Box>

			{/* View prompt modal: System prompt + Context tabs */}
			<Modal isOpen={isSystemPromptModalOpen} onClose={onSystemPromptModalClose} size="4xl">
				<ModalOverlay />
				<ModalContent maxH="85vh">
					<ModalHeader>View prompt</ModalHeader>
					<ModalCloseButton />
					<ModalBody pb={6} overflow="hidden" display="flex" flexDirection="column">
						<Tabs size="sm" flex={1} display="flex" flexDirection="column" minH={0}>
							<TabList>
								<Tab>System prompt</Tab>
								<Tab>Context</Tab>
							</TabList>
							<TabPanels flex={1} overflow="auto" minH={0}>
								<TabPanel px={0} pt={3}>
									<Flex justify="flex-end" mb={2}>
										<CopyButton value={currentSystemPrompt} />
									</Flex>
									<Textarea
										value={currentSystemPrompt}
										readOnly
										minH="400px"
										resize="vertical"
										bg="gray.50"
										border="1px solid"
										borderColor="gray.200"
										fontFamily="mono"
										fontSize="sm"
									/>
								</TabPanel>
								<TabPanel px={0} pt={3}>
									<Flex justify="flex-end" mb={2}>
										<CopyButton value={currentContext ?? ""} />
									</Flex>
									<Textarea
										value={currentContext ?? ""}
										readOnly
										minH="400px"
										resize="vertical"
										bg="gray.50"
										border="1px solid"
										borderColor="gray.200"
										fontFamily="mono"
										fontSize="sm"
										placeholder="No context template configured"
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</ModalBody>
				</ModalContent>
			</Modal>

			{/* Save Chat Modal */}
			<Modal isOpen={isSaveModalOpen} onClose={onSaveModalClose} size="md" closeOnOverlayClick={!isSaving}>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader>Save Chat</ModalHeader>
					{!isSaving && <ModalCloseButton />}
					<ModalBody>
						{isSaving ? (
							<VStack spacing={4} py={6}>
								<Spinner size="lg" color="green.500" thickness="3px" />
								<Text color="gray.600" fontSize="md">Saving your chat...</Text>
							</VStack>
						) : (
							<>
								<FormControl>
									<FormLabel>Chat Title</FormLabel>
									<Input
										placeholder="Enter a title for this chat..."
										value={saveChatTitle}
										onChange={(e) => setSaveChatTitle(e.target.value)}
										autoFocus
									/>
								</FormControl>
								<FormControl>
									<FormLabel>Chat Description</FormLabel>
									<Input
										placeholder="Enter a description for this chat..."
										value={saveChatDescription}
										onChange={(e) => setSaveChatDescription(e.target.value)}
									/>
								</FormControl>
							</>
						)}
					</ModalBody>
					{!isSaving && (
						<ModalFooter>
							<Button variant="ghost" mr={3} onClick={onSaveModalClose}>
								Cancel
							</Button>
							<Button
								colorScheme="green"
								isDisabled={!saveChatTitle.trim()}
								onClick={async () => {
									if (!chatId || !saveChatTitle.trim()) return;
									setIsSaving(true);
									try {
										// Save with title in single API call
										await saveSession(chatId, saveChatTitle.trim(), saveChatDescription?.trim());
										onSaveModalClose();
										setSaveChatTitle("");
										setSaveChatDescription("");
										setCurrentSessionHeader(undefined);
										setIsChatSaved(true);
										// Refresh the chat sessions list
										if (isFromEva) {
											await fetchEvaChatSessionsList("");
											if (isFromEva && chatId) {
												navigate(`/eva/chat/${chatId}`);
											}
										} else {
											await fetchChatSessionsList(ChatClientType.FRIDA, "");
										}
									} catch (error) {
										console.error("Error saving chat:", error);
									} finally {
										setIsSaving(false);
									}
								}}
							>
								Save
							</Button>
						</ModalFooter>
					)}
				</ModalContent>
			</Modal>

		</Box>
	);
};

export default ChatPage;